"use client";

import { Coins, Info } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselItem,
} from "@/components/ui/carousel";
import { Skeleton } from "@/components/ui/skeleton";
import { useBtcPrice } from "@/hooks/useBtcPrice";
import { useLanguage } from "@/hooks/useLanguage";
import { cn } from "@/lib/utils";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import { useSponsoredRanking } from "./hooks/use-sponsored-ranking";
import { PosterLoadingPreview } from "./poster-loading-preview";
import { getPosterDataUri } from "./poster";
import { PromoDialog } from "./promo-dialog";
import { RankingItem } from "./ranking-item";
import type { SponsoredHolding } from "./types";
import {
	formatHoldingUsdValue,
	formatHoldingUsdValueFull,
} from "./utils";

const uiLog = createLogAdapter(
	"app/plaza/components/sponsored-ranking-banner/index.tsx",
);

function useReducedMotion() {
	const [reduced, setReduced] = useState(false);

	useEffect(() => {
		const media = window.matchMedia("(prefers-reduced-motion: reduce)");
		const update = () => setReduced(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	return reduced;
}

function useDesktopViewport() {
	const [isDesktop, setIsDesktop] = useState(() =>
		typeof window !== "undefined"
			? window.matchMedia("(min-width: 1024px)").matches
			: false,
	);

	useEffect(() => {
		const media = window.matchMedia("(min-width: 1024px)");
		const update = () => setIsDesktop(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	return isDesktop;
}

function PosterMetricBadge({
	holding,
	btcPrice,
	rank,
	className,
}: {
	holding: SponsoredHolding;
	btcPrice: number;
	rank: number;
	className?: string;
}) {
	const accentPalette =
		rank === 0
			? {
					border: "border-[#F5D77B]/35",
					sheen: "bg-[linear-gradient(135deg,rgba(245,215,123,0.14),transparent_38%,rgba(255,255,255,0.05)_52%,transparent_72%)]",
					sideGlow: "bg-[linear-gradient(180deg,rgba(245,215,123,0.18),rgba(245,215,123,0.06))]",
					iconBorder: "border-[#F5D77B]/28",
					iconBg:
						"bg-[radial-gradient(circle_at_30%_30%,rgba(255,244,200,0.3),rgba(196,142,39,0.16)_45%,rgba(14,18,28,0.28)_100%)]",
					iconColor: "text-[#F7D774]",
					iconShadow: "drop-shadow-[0_0_8px_rgba(247,215,116,0.55)]",
				}
			: rank === 1
				? {
						border: "border-[#DCE6F7]/35",
						sheen: "bg-[linear-gradient(135deg,rgba(220,230,247,0.14),transparent_38%,rgba(255,255,255,0.05)_52%,transparent_72%)]",
						sideGlow: "bg-[linear-gradient(180deg,rgba(220,230,247,0.18),rgba(126,163,214,0.08))]",
						iconBorder: "border-[#DCE6F7]/28",
						iconBg:
							"bg-[radial-gradient(circle_at_30%_30%,rgba(241,246,255,0.28),rgba(130,164,212,0.16)_45%,rgba(14,18,28,0.28)_100%)]",
						iconColor: "text-[#DCE6F7]",
						iconShadow: "drop-shadow-[0_0_8px_rgba(220,230,247,0.45)]",
					}
				: {
						border: "border-[#D9A17A]/35",
						sheen: "bg-[linear-gradient(135deg,rgba(217,161,122,0.14),transparent_38%,rgba(255,255,255,0.05)_52%,transparent_72%)]",
						sideGlow: "bg-[linear-gradient(180deg,rgba(217,161,122,0.18),rgba(155,100,72,0.08))]",
						iconBorder: "border-[#D9A17A]/28",
						iconBg:
							"bg-[radial-gradient(circle_at_30%_30%,rgba(245,214,189,0.28),rgba(181,113,77,0.16)_45%,rgba(14,18,28,0.28)_100%)]",
						iconColor: "text-[#E2B18D]",
						iconShadow: "drop-shadow-[0_0_8px_rgba(217,161,122,0.45)]",
					};

	return (
		<div
			className={cn(
				"pointer-events-none absolute left-4 top-4 z-10 inline-flex items-center gap-2.5 overflow-hidden rounded-[18px] border bg-[linear-gradient(180deg,rgba(10,14,24,0.9),rgba(8,12,20,0.72))] px-3.5 py-2.5 text-white shadow-[0_14px_32px_rgba(0,0,0,0.34),inset_0_1px_0_rgba(255,255,255,0.1)] backdrop-blur-md",
				accentPalette.border,
				className,
			)}
		>
			<span className={cn("absolute inset-0", accentPalette.sheen)} />
			<span className={cn("absolute inset-y-[1px] left-[1px] w-[36px] rounded-l-[17px]", accentPalette.sideGlow)} />
			<div
				className={cn(
					"relative flex h-8 w-8 shrink-0 items-center justify-center rounded-[11px] border shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_6px_18px_rgba(0,0,0,0.16)]",
					accentPalette.iconBorder,
					accentPalette.iconBg,
				)}
			>
				<Coins className={cn("h-[18px] w-[18px]", accentPalette.iconColor, accentPalette.iconShadow)} />
			</div>
			<div className="relative flex min-w-0 flex-col justify-center">
				<p
					className="truncate text-sm font-black leading-none tracking-[0.01em] text-white drop-shadow-[0_1px_8px_rgba(0,0,0,0.35)]"
					title={formatHoldingUsdValueFull(holding, btcPrice)}
				>
					{formatHoldingUsdValue(holding, btcPrice)}
				</p>
			</div>
		</div>
	);
}

function SponsoredRankingBannerSkeleton({
	className,
}: {
	className?: string;
}) {
	return (
		<div className={cn("w-full", className)}>
			<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
				<Skeleton className="h-[320px] rounded-[30px] lg:h-[360px]" />
				<div className="space-y-3">
					<Skeleton className="h-[94px] rounded-[24px]" />
					<Skeleton className="h-[94px] rounded-[24px]" />
					<Skeleton className="h-[94px] rounded-[24px]" />
					<Skeleton className="h-12 rounded-2xl" />
				</div>
			</div>
		</div>
	);
}

export function SponsoredRankingBanner({ className }: { className?: string }) {
	const { getTranslation, language } = useLanguage();
	const { holdings, loading, error } = useSponsoredRanking(language);
	const { btcPrice } = useBtcPrice();
	const { theme, systemTheme } = useTheme();
	const [api, setApi] = useState<CarouselApi>();
	const [activeIndex, setActiveIndex] = useState(0);
	const [promoOpen, setPromoOpen] = useState(false);
	const [desktopPosterMap, setDesktopPosterMap] = useState<Record<string, string | null>>({});
	const [mobilePosterMap, setMobilePosterMap] = useState<Record<string, string | null>>({});
	const [progress, setProgress] = useState(0);
	const [isPaused, setIsPaused] = useState(false);
	const reducedMotion = useReducedMotion();
	const isDesktopViewport = useDesktopViewport();
	const durationsRef = useRef([10000, 6000, 4000]);
	const bannerHoldings = holdings.slice(0, 3);

	const resolvedTheme =
		theme === "system" ? (systemTheme === "dark" ? "dark" : "light") : theme === "dark" ? "dark" : "light";
	const activeVariant = isDesktopViewport ? "desktop" : "mobile";

	useEffect(() => {
		if (!bannerHoldings.length) return;

		let disposed = false;
		const idleCallbacks: number[] = [];
		const timeoutIds: ReturnType<typeof globalThis.setTimeout>[] = [];

		const updatePosterMap = (tokenId: string, nextPoster: string | null) => {
			if (!nextPoster || disposed) return;

			if (activeVariant === "desktop") {
				setDesktopPosterMap((prev) =>
					prev[tokenId] === nextPoster ? prev : { ...prev, [tokenId]: nextPoster },
				);
				return;
			}

			setMobilePosterMap((prev) =>
				prev[tokenId] === nextPoster ? prev : { ...prev, [tokenId]: nextPoster },
			);
		};

		const loadPoster = async (holding: (typeof bannerHoldings)[number], rank: number) => {
			const tokenId = holding.token.id;
			const poster = await getPosterDataUri({
				holding,
				rank,
				theme: resolvedTheme,
				variant: activeVariant,
				language,
				onRevalidated: (nextPoster) => updatePosterMap(tokenId, nextPoster),
			});

			updatePosterMap(tokenId, poster);
		};

		const scheduleIdle = (callback: () => void) => {
			if ("requestIdleCallback" in window) {
				const id = window.requestIdleCallback(callback, { timeout: 1200 });
				idleCallbacks.push(id);
				return;
			}

			const id = globalThis.setTimeout(callback, 180);
			timeoutIds.push(id);
		};

		const prioritizedHoldings = bannerHoldings
			.map((holding, index) => ({ holding, index }))
			.sort((left, right) =>
				left.index === activeIndex ? -1 : right.index === activeIndex ? 1 : left.index - right.index,
			);

		void loadPoster(
			prioritizedHoldings[0]?.holding ?? bannerHoldings[0],
			prioritizedHoldings[0]?.index ?? 0,
		);

		for (const entry of prioritizedHoldings.slice(1)) {
			scheduleIdle(() => {
				void loadPoster(entry.holding, entry.index);
			});
		}

		return () => {
			disposed = true;
			for (const id of idleCallbacks) {
				window.cancelIdleCallback?.(id);
			}
			for (const id of timeoutIds) {
				globalThis.clearTimeout(id);
			}
		};
	}, [activeIndex, activeVariant, bannerHoldings, language, resolvedTheme]);

	useEffect(() => {
		if (!api) return;
		const onSelect = () => setActiveIndex(api.selectedScrollSnap());
		onSelect();
		api.on("select", onSelect);
		return () => {
			api.off("select", onSelect);
		};
	}, [api]);

	useEffect(() => {
		setProgress(0);
	}, [activeIndex]);

	useEffect(() => {
		if (reducedMotion || bannerHoldings.length <= 1) {
			setProgress(1);
			return;
		}

		if (isPaused) return;

		const duration = durationsRef.current[Math.min(activeIndex, durationsRef.current.length - 1)];
		let frameId = 0;
		let start = performance.now();

		const tick = (now: number) => {
			const elapsed = now - start;
			const nextProgress = Math.min(elapsed / duration, 1);
			setProgress(nextProgress);

			if (nextProgress >= 1) {
				api?.scrollTo((activeIndex + 1) % bannerHoldings.length);
				return;
			}

			frameId = window.requestAnimationFrame(tick);
		};

		frameId = window.requestAnimationFrame(tick);

		return () => {
			window.cancelAnimationFrame(frameId);
		};
	}, [activeIndex, api, bannerHoldings.length, isPaused, reducedMotion]);

	const handlePause = () => setIsPaused(true);
	const handleResume = () => setIsPaused(false);
	const handleSelectSlide = (index: number) => {
		setActiveIndex(index);
		setProgress(0);
		api?.scrollTo(index);
	};

	if (loading) {
		return <SponsoredRankingBannerSkeleton className={className} />;
	}

	if (error || bannerHoldings.length === 0) {
		return <SponsoredRankingBannerSkeleton className={className} />;
	}

	return (
		<>
			<section
				className={cn(
					"relative overflow-hidden rounded-[32px] p-0 lg:border lg:border-light-border lg:bg-light-surface/85 lg:p-3 lg:shadow-[0_18px_50px_rgba(17,24,39,0.08)] lg:backdrop-blur-sm dark:lg:border-dark-border dark:lg:bg-dark-surface/90 dark:lg:shadow-[0_24px_70px_rgba(0,0,0,0.35)]",
					className,
				)}
				aria-label={getTranslation("plaza.sponsoredRankingBanner")}
			>
				<button
					type="button"
					onClick={() => setPromoOpen(true)}
					className="absolute right-0 top-0 z-20 h-16 w-16 lg:hidden"
					aria-label={getTranslation("plaza.supportRanking")}
				>
					<span className="absolute inset-0" />
					<span
						className="absolute right-0 top-0 h-[50px] w-[50px] bg-[#CFECCF] shadow-[inset_0_-1px_0_rgba(255,255,255,0.7)] dark:bg-[#9ED29F]"
						style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }}
					/>
					<span
						className="absolute right-0 top-0 h-[44px] w-[44px] bg-gradient-to-br from-light-primary via-[#54B65C] to-[#26753D] shadow-[0_8px_14px_rgba(38,117,61,0.24)] dark:from-dark-primary dark:via-[#51AE59] dark:to-[#236837]"
						style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }}
					/>
					<span
						className="absolute right-[14px] top-[8px] h-[24px] w-[1px] bg-black/15 dark:bg-black/20"
						style={{ transform: "rotate(45deg)", transformOrigin: "top center" }}
					/>
					<span
						className="absolute right-[6px] top-[12px] text-[10px] font-extrabold tracking-[0.14em] text-white"
						style={{ transform: "rotate(45deg)" }}
						>
							{getTranslation("plaza.support")}
						</span>
					</button>
				<div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-3">
					<div
						onMouseEnter={handlePause}
						onMouseLeave={handleResume}
						onFocus={handlePause}
						onBlur={handleResume}
					>
						<Carousel
							setApi={setApi}
							opts={{ align: "start", loop: bannerHoldings.length > 1 }}
							className="w-full"
						>
							<CarouselContent className="-ml-0">
								{bannerHoldings.map((holding, index) => (
									<CarouselItem key={holding.token.id} className="pl-0">
										<Link
											href={`/plaza/token/${holding.token.id}`}
											className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-light-accent/60 dark:focus-visible:ring-dark-accent/60"
										>
											<div className="relative hidden h-[360px] overflow-hidden rounded-[30px] lg:block">
												{desktopPosterMap[holding.token.id] ? (
													<Image
														src={desktopPosterMap[holding.token.id] as string}
														alt={`${holding.token.ticker} sponsored poster`}
														fill
														priority={index === 0}
														sizes="880px"
														className="object-cover transition-transform duration-500 group-hover:scale-[1.015]"
														unoptimized
													/>
												) : (
													<PosterLoadingPreview
														holding={holding}
														rank={index}
														variant="desktop"
														language={language}
														label={getTranslation("plaza.generatingPoster")}
													/>
												)}
												<PosterMetricBadge
													holding={holding}
													btcPrice={btcPrice}
													rank={index}
												/>
												<div className="absolute inset-x-0 bottom-0 h-[3px] bg-black/10 dark:bg-white/10">
													<div
														className="h-full origin-left"
														style={{
															width: `${activeIndex === index ? progress * 100 : 0}%`,
															background:
																index === 0
																	? "linear-gradient(90deg, #F7D774 0%, #4CAF50 100%)"
																	: index === 1
																		? "linear-gradient(90deg, #E8EDF5 0%, #2979FF 100%)"
																		: "linear-gradient(90deg, #D9A17A 0%, #4CAF50 100%)",
															boxShadow:
																index === 0
																	? "0 0 12px rgba(247,215,116,0.75)"
																	: index === 1
																		? "0 0 12px rgba(41,121,255,0.7)"
																		: "0 0 12px rgba(217,161,122,0.7)",
														}}
													/>
												</div>
											</div>
											<div className="relative aspect-[3/2] overflow-hidden rounded-[30px] lg:hidden">
												{mobilePosterMap[holding.token.id] ? (
													<Image
														src={mobilePosterMap[holding.token.id] as string}
														alt={`${holding.token.ticker} sponsored mobile poster`}
														fill
														priority={index === 0}
														sizes="100vw"
														className="object-cover transition-transform duration-500"
														unoptimized
													/>
												) : (
													<PosterLoadingPreview
														holding={holding}
														rank={index}
														variant="mobile"
														language={language}
														label={getTranslation("plaza.generatingPoster")}
													/>
												)}
												<PosterMetricBadge
													holding={holding}
													btcPrice={btcPrice}
													rank={index}
													className="left-3 top-3 origin-top-left scale-[0.88] gap-2 rounded-2xl px-2.5 py-2"
												/>
												<div className="absolute inset-x-0 bottom-0 h-[3px] bg-black/10 dark:bg-white/10">
													<div
														className="h-full origin-left"
														style={{
															width: `${activeIndex === index ? progress * 100 : 0}%`,
															background:
																index === 0
																	? "linear-gradient(90deg, #F7D774 0%, #4CAF50 100%)"
																	: index === 1
																		? "linear-gradient(90deg, #E8EDF5 0%, #2979FF 100%)"
																		: "linear-gradient(90deg, #D9A17A 0%, #4CAF50 100%)",
															boxShadow:
																index === 0
																	? "0 0 12px rgba(247,215,116,0.75)"
																	: index === 1
																		? "0 0 12px rgba(41,121,255,0.7)"
																		: "0 0 12px rgba(217,161,122,0.7)",
														}}
													/>
												</div>
											</div>
										</Link>
									</CarouselItem>
								))}
							</CarouselContent>
						</Carousel>
					</div>
						<div className="hidden flex-col gap-3 lg:flex">
							{bannerHoldings.map((holding, index) => (
								<RankingItem
									key={holding.token.id}
									holding={holding}
									rank={index}
									active={activeIndex === index}
									onClick={() => handleSelectSlide(index)}
									href={`/plaza/token/${holding.token.id}`}
									btcPrice={btcPrice}
								/>
							))}
							<Button
								type="button"
								onClick={() => setPromoOpen(true)}
								className="mt-auto h-12 cursor-pointer rounded-2xl bg-gradient-to-r from-light-primary to-light-accent text-white shadow-md hover:opacity-95 dark:from-dark-primary dark:to-dark-accent"
							>
								<Info className="h-4 w-4" />
								助力冲榜
							</Button>
						</div>
				</div>
			</section>

			<PromoDialog
				open={promoOpen}
				onOpenChange={setPromoOpen}
				holdings={holdings}
				btcPrice={btcPrice}
			/>
		</>
	);
}
